import { type ReactNode, useEffect, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import InputBase from '@mui/material/InputBase';
import {
  useActivePractice,
  useDispatch,
  usePracticeForms,
} from '@witmetrics/data-store';
import { isEmpty } from '@witmetrics/utils/misc';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { useString } from '@/hooks/useString';
import SlideTransition from '@/components/SlideTransition';
import LoopingEllipsis from '@/components/LoopingEllipsis';
import ResultsList from './ResultsList';
import { classes, fetchPracticeForms, filterForms } from './utils';

export type FormSearchDialogProps = {
  onSelect: (formId: number) => void;
  onClose: () => void;
};

export default function FormSearchDialog({
  onSelect,
  onClose,
}: FormSearchDialogProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const activePractice = useActivePractice();
  const forms = usePracticeForms(activePractice?.id);
  const [search, setSearch] = useString();
  const [isFetchingData, toggleFetchingData] = useToggle(true);

  useEffect(() => {
    fetchData();
  }, [activePractice]);

  const fetchData = async () => {
    try {
      toggleFetchingData(true);
      dispatch.addForms(await fetchPracticeForms(activePractice!.id));
      toggleFetchingData(false);
    } catch (err) {
      toggleFetchingData(false);
      onApiError(err, 'Error fetching forms', () => fetchData());
    }
  };

  const handleSelect = (formId: number) => {
    onSelect(formId);
  };

  const renderMessage = (message: ReactNode) => (
    <div className={classes.message}>{message}</div>
  );

  const renderResults = () => {
    if (isFetchingData) {
      return renderMessage(
        <>
          Fetching forms
          <LoopingEllipsis />
        </>
      );
    } else if (isEmpty(forms)) {
      return renderMessage(`No forms found for ${activePractice!.name}.`);
    }
    const results = filterForms(forms, search);
    if (isEmpty(results)) {
      return renderMessage('No forms matching current search.');
    }
    return <ResultsList results={results} onSelect={handleSelect} />;
  };

  return (
    <Dialog
      open
      keepMounted
      disableRestoreFocus
      maxWidth={false}
      PaperProps={{ className: classes.paper }}
      TransitionComponent={SlideTransition}
      onClose={onClose}>
      <div className={classes.wrapper}>
        <InputBase
          ref={inputRef}
          autoFocus
          className={classes.inputBase}
          autoComplete="off"
          name="forms-search"
          placeholder="Search for forms by name or description..."
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {renderResults()}
      </div>
    </Dialog>
  );
}
